"use client";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import FAQ from "@/components/FAQ";
import { FlexColumnAlignJustifyCenter } from "@/components/Flex";
import Footer from "@/components/Footer";
import LinkWrapper from "@/components/wrappers/Link";
import { BulkPlans } from "@/features/BulkPricingPage/components/BulkPricingComponents";
import br from "@/styles/breakpoints.module.css";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import clsx from "clsx";
import { ReviewType } from "../../LandingMainPage/constants";
import UsersFeedbacks from "../../UploadLandingPages/components/UsersFeedbacks";
import styles from "../../UploadLandingPages/components/medias/video-summarizer.module.css";
import CompliantsSection from "../../components/CompliantsSection";
import SectionContainer from "../../components/SectionContainer";
import { LandingHalfSection, LandingMainContainer, LandingPageWrapper } from "../../components/styled/Containers";
import CompetitorsComparisonTable from "../components/CompetitorsComparisionTable";
import SwitchingBenefits from "../components/SwitchingBenefits";

const faqs = [
    {
        question: "Why is Knowt the best SchoolAI alternative?",
        answer: "3 million+ students & teachers have already switched to Knowt and trust it for good reason! Like School AI, Knowt offers tons of AI teacher tools designed to save time for educators. On top of these tools, Knowt has unique AI tools that are more interactive and gamified like the PDF, PPT, & Video Summarizers and a Live Lecture Note Taker that take your files and turn them into study material instantly. On Knowt, your students can also can create & share notes and flashcards and browse over 4 million notes, flashcards, & study materials created by fellow users. Not to mention the ability to do unlimited studying with free Learn, Matching, & Spaced Repetition modes and access AP Study Guides and unlimited AP practice questions. Ready to start? Click the “Contact us” button at the top of this page!",
    },
    {
        question: "Is SchoolAI free to use?",
        answer: "SchoolAI offers a limited free tier before requiring teachers to pay $15 per month. Knowt provides a much more affordable alternative with over a 50% discount on subscriptions and significantly more AI features, making it a better option for both individual teachers and enterprises.",
    },
    {
        question: "What’s the Knowt Pilot Program?",
        answer: "This is basically a chance for you to roll out Knowt to your teachers & make sure they actually love it before making any commitments. Once you fill out the pilot program form above, we’ll set you up with as many licenses as you like. Share them with your students, and let them test out all our AI tools for free for the rest of the school year. At the end of the pilot, if they love it & you choose to stay with Knowt, you’ll lock in a price that 50% cheaper for your school. It’s a win-win for you & your teachers!",
    },
    {
        question: "I have an existing contract with SchoolAI. How do I switch for $0?",
        answer: "Just fill out the form at the top of this page & tell us a little about your school! We’ll reach out to you within a few hours with all the details on free teacher licenses for your school’s first year with Knowt. We’ll also work with you to educate your teachers on Knowt during that time period so they can make the most of it.",
    },
    {
        question: "I used to pay for a School AI contract & switched to Knowt. What happens after the first year?",
        answer: "When you switched from your School contract, you automatically got the first year of Knowt completely free for you. Once that’s over, you can extend your contract with us & we’ll match School AI’s price or beat it. We’ll also lock that cheaper price in for the next few years so you don’t have to worry about unexpected pay increases. ",
    },
    {
        question: "How does Knowt’s AI for teachers support student learning?",
        answer: "AI for education enhances student learning by personalizing instruction, providing instant feedback, and automating repetitive tasks, allowing teachers to focus on engagement and critical thinking. AI-powered tools can adapt to each student's learning style by generating differentiated lesson plans, study guides, and practice questions based on their needs. Additionally, AI-powered writing and feedback tools help students improve their skills through real-time grammar suggestions, content analysis, and structured guidance - feedback that can often be cumbersome to write. With AI, students benefit from more engaging, customized, and efficient learning experiences, while teachers can monitor their progress more effectively.",
    },
    {
        question: "Can Knowt’s AI for teachers create personalized learning materials?",
        answer: "Yes! AI for education can generate customized study guides, reading passages, and differentiated lesson plans based on student learning levels and subject matter. With AI teacher tools, educators can adjust reading difficulty, tailor quizzes, and provide targeted learning support for every student.",
    },
];

const SchoolAIPage = () => {
    return (
        <LandingPageWrapper>
            <LandingMainContainer
                accentColorDark="none"
                accentColorLight="none"
                style={{ backgroundColor: themeColors.neutralWhite, paddingBottom: spacing.MD }}>
                <SectionContainer
                    isPageHeader
                    title="Switch from School AI for $0"
                    subtitle="Considering School AI, or already have a contract? Knowt has more for 50% cheaper! Click below, and get unlimited licenses for 6 months with no contract."
                    titleStyle={{ fontWeight: "800" }}
                    subtitleStyle={{ maxWidth: "90rem" }}>
                    <FlexColumnAlignJustifyCenter style={{ gap: spacing.SM }}>
                        <LinkWrapper
                            href={"https://knowt.com/free-ai-tools-for-teachers"}
                            target="_blank"
                            onClick={async () => await Mixpanel.track("See AI Tools Button - Clicked")}>
                            <CircularRectTextButton
                                component={"div"}
                                className="bodyBold1"
                                style={{
                                    backgroundColor: themeColors.primary,
                                    color: themeColors.primaryDark,
                                    padding: `${spacing.SM} ${spacing.MD}`,
                                }}>
                                {"Try our AI Tools for Teachers"}
                            </CircularRectTextButton>
                        </LinkWrapper>
                        <LinkWrapper
                            href={"https://form.fillout.com/t/vbsifgi1m2us"}
                            target="_blank"
                            onClick={async () => await Mixpanel.track("Pilot Program Button - Clicked")}>
                            <span className="bodyBold1">Admin? Start the pilot to get free licenses for 6 months</span>
                        </LinkWrapper>
                    </FlexColumnAlignJustifyCenter>
                    <CompetitorsComparisonTable primaryCompetitor="SchoolAI" />
                </SectionContainer>
            </LandingMainContainer>
            <LandingMainContainer
                accentColorDark="none"
                accentColorLight="none"
                style={{ backgroundColor: themeColors.background }}>
                <SectionContainer
                    title="Have an existing contract? Get 1 full school year for $0"
                    subtitle="Switch from their contract to ours and lock in 1 free school year, and 50% cheaper pricing than Magic School for the next 3 years."
                    sectionStyle={{ maxWidth: "unset" }}
                    headerStyle={{ maxWidth: "103rem" }}>
                    <LinkWrapper href={"knowt.com/schools/bulk-pricing"}>
                        <CircularRectTextButton
                            component="div"
                            style={{
                                padding: "1.8rem 2.4rem",
                                color: themeColors.primaryDark,
                                backgroundColor: themeColors.primary,
                            }}>
                            {"Switch for free!"}
                        </CircularRectTextButton>
                    </LinkWrapper>
                    <BulkPlans />
                </SectionContainer>
            </LandingMainContainer>
            <LandingMainContainer
                accentColorDark="none"
                accentColorLight="none"
                style={{ backgroundColor: themeColors.neutralWhite }}>
                <SwitchingBenefits competitor={"SchoolAI"} />
            </LandingMainContainer>
            <LandingMainContainer
                accentColorDark="none"
                accentColorLight="none"
                className={clsx([styles.feedbackContainer, br.mdDownDisplayNone])}
                style={{ backgroundColor: themeColors.background, overflow: "hidden", paddingBottom: 0 }}>
                <UsersFeedbacks
                    type={ReviewType.SCHOOLS}
                    title="Trusted by 1,000+ schools"
                    isForSchools
                    subtitle={"Join our thriving community of teachers & students that trust Knowt."}
                />
            </LandingMainContainer>
            <LandingMainContainer
                accentColorDark="none"
                accentColorLight="none"
                style={{ backgroundColor: themeColors.neutralWhite }}>
                <CompliantsSection onWhiteBackground />
            </LandingMainContainer>
            <LandingMainContainer
                accentColorLight="none"
                accentColorDark="none"
                style={{
                    padding: 0,
                    backgroundColor: themeColors.neutralWhite,
                }}>
                <LandingHalfSection>
                    <FAQ
                        faqs={faqs}
                        title={"FAQ"}
                        description={"We thought you might have some questions..."}
                        titleClassName="heading2"
                        containerStyle={{
                            marginTop: 0,
                            backgroundColor: themeColors.neutralWhite,
                        }}
                    />
                </LandingHalfSection>
            </LandingMainContainer>
            <Footer landing />
        </LandingPageWrapper>
    );
};

export default SchoolAIPage;
